import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SaIndustryCard } from "@/components/superadmin/SaIndustryCard";
import { SaDepartmentsCard } from "@/components/superadmin/SaDepartmentsCard";
import { PosOnboardingDialog } from "@/components/superadmin/PosOnboardingDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { MigrationWizard } from "@/pages/ImportData";
import HelpAdmin from "@/pages/HelpAdmin";
import WhatsNewAdmin from "@/pages/WhatsNewAdmin";
import AddCompanyDialog from "@/components/AddCompanyDialog";
import ServiceClientDrawer, { STATUS_COLOR as SVC_STATUS_COLOR } from "@/components/ServiceClientDrawer";
import { NonBillableControl, NonBillableBadge } from "@/components/superadmin/NonBillableControl";
import { BillingRestrictedBadge } from "@/components/superadmin/BillingRestrictedBadge";
import EnvBadge from "@/components/EnvBadge";
import BillingDashboard from "@/components/BillingDashboard";
import BillingModal from "@/components/BillingModal";
import PlatformBillingSettings from "@/components/PlatformBillingSettings";
import TenantUsersModal from "@/components/EmergencyPinResetDialog";
import EntitlementsDialog from "@/components/EntitlementsDialog";
import PlatformSummary from "@/components/superadmin/PlatformSummary";
import PosClientCard from "@/components/superadmin/PosClientCard";
import ServiceClientCard from "@/components/superadmin/ServiceClientCard";
import { LegacyFooter } from "@/pages/Legacy";
import {
  Building2, Plus, Copy, Zap, LogOut, CreditCard,
  MessageSquare, Send, LifeBuoy, RadioTower, Bug, Lightbulb, ShieldCheck, Boxes, Sparkles, KeyRound, Trash2, FlaskConical, Fingerprint, Database,
} from "lucide-react";

const blank = { owner_name: "", business_name: "", email: "", phone: "" };

function fmtBytes(b) {
  if (b === null || b === undefined) return "—";
  if (b < 1024) return `${b} B`;
  const u = ["KB", "MB", "GB", "TB"]; let i = -1; let n = b;
  do { n /= 1024; i++; } while (n >= 1024 && i < u.length - 1);
  return `${n.toFixed(1)} ${u[i]}`;
}

function UsagePanel({ usage, includeInternal }) {
  if (!usage) return null;
  const { businesses: bz, totals, storage, status, growth } = usage;
  const statusMap = {
    "Healthy": "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
    "Monitor": "bg-amber-500/15 text-amber-600 border-amber-500/30",
    "Upgrade Soon": "bg-rose-500/15 text-rose-600 border-rose-500/30",
  };
  const STATUS_LABEL = { past_due: "Past due", cancelled: "Cancelled", onboarding: "Awaiting activation", awaiting_payment: "Awaiting payment", unknown: "No status" };
  const otherEntries = Object.entries(bz.other || {});
  const otherTotal = otherEntries.reduce((a, [, n]) => a + n, 0);
  const Stat = ({ label, value, testid, hint }) => (
    <div className="rounded-xl border border-border p-3 min-w-0" data-testid={testid}>
      <p className="text-2xl font-head font-extrabold tabular-nums truncate">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      {hint && <p className="text-[11px] text-muted-foreground/80 mt-0.5 truncate" title={hint}>{hint}</p>}
    </div>
  );
  const CapBar = ({ label, used, limitMb, pct, testid }) => (
    <div className="rounded-xl border border-border p-3" data-testid={testid}>
      <div className="flex justify-between items-baseline gap-2 text-xs"><span className="text-muted-foreground">{label}</span>
        <span className="font-semibold text-sm tabular-nums shrink-0">{fmtBytes(used)}{limitMb ? ` / ${limitMb} MB` : ""}</span></div>
      {pct !== null && pct !== undefined && (
        <>
          <div className="h-2 rounded-full bg-secondary overflow-hidden mt-2">
            <div className={`h-full ${pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">{pct}% of plan limit used</p>
        </>
      )}
    </div>
  );
  const posN = bz.by_product?.neec_pos || 0, svcN = bz.by_product?.neec_service || 0;
  return (
    <div className="pt-4" data-testid="usage-panel">
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><Boxes className="w-6 h-6" /><h2 className="font-head text-2xl font-extrabold">Platform Usage &amp; Capacity</h2></div>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="usage-scope">{includeInternal ? "Including internal test accounts (NEEC Test Hardware, QA Sandbox)." : "Production customer activity. Internal test accounts excluded."}</p>
        </div>
        {status && <span className={`text-xs font-bold px-3 py-1 rounded-full border ${statusMap[status]}`} data-testid="usage-status" title="Storage capacity vs configured plan limit">Capacity: {status}</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Stat label="Client Businesses" value={bz.total} testid="usage-biz-total" hint={`${posN} P-O-S · ${svcN} Service`} />
        <Stat label="Active" value={bz.active} testid="usage-biz-active" />
        <Stat label="Trial" value={bz.trial} testid="usage-biz-trial" />
        <Stat label="Suspended" value={bz.suspended} testid="usage-biz-suspended" hint={otherTotal ? `+${otherTotal} other: ${otherEntries.map(([k, n]) => `${STATUS_LABEL[k] || k} ${n}`).join(", ")}` : undefined} />
        <Stat label="Users / Staff" value={totals.users} testid="usage-users" />
        <Stat label="Products" value={totals.products} testid="usage-products" />
        <Stat label="Customers" value={totals.customers} testid="usage-customers" />
        <Stat label="Sales / Invoices" value={totals.invoices} testid="usage-invoices" />
      </div>
      {svcN > 0 && <p className="text-[11px] text-muted-foreground mb-3" data-testid="usage-pos-note">Users, Products, Customers and Sales count NEEC P-O-S tenants only; NEEC Service businesses keep their data in the Service platform.</p>}
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <CapBar label="Estimated database storage" used={storage.db_bytes} limitMb={storage.db_limit_mb} pct={storage.db_percent} testid="usage-db-storage" />
        <CapBar label="File / image storage" used={storage.file_bytes} limitMb={storage.file_limit_mb} pct={storage.file_percent} testid="usage-file-storage" />
      </div>
      <div className="rounded-xl border border-border p-3 flex flex-wrap gap-x-8 gap-y-1 text-sm" data-testid="usage-growth">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground w-full">This month</span>
        <span>New tenants: <b>{growth.new_tenants}</b></span>
        <span>New users: <b>{growth.new_users}</b></span>
        <span>New products: <b>{growth.new_products}</b></span>
      </div>
    </div>
  );
}

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [lastLink, setLastLink] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [wnOpen, setWnOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [svcSel, setSvcSel] = useState(null);
  const [filter, setFilter] = useState("all");
  const [usage, setUsage] = useState(null);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [overview, setOverview] = useState(null);
  const loadOverview = (inc) => api.get(`/superadmin/overview?include_internal=${inc}`).then((r) => setOverview(r.data)).catch(() => {});
  const loadUsage = (inc) => api.get(`/superadmin/platform-usage?include_internal=${inc}`).then((r) => setUsage(r.data)).catch(() => {});

  // Super Admin self-service password change
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ cur: "", np: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  // Super Admin account security (2FA, recovery codes, email change)
  const [secOpen, setSecOpen] = useState(false);
  const [sec, setSec] = useState(null);
  const [setup, setSetup] = useState(null);
  const [enableCode, setEnableCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [step, setStep] = useState({ password: "", code: "" });
  const [newEmail, setNewEmail] = useState("");
  const loadSec = () => api.get("/auth/security").then((r) => setSec(r.data)).catch(() => {});
  const openSecurity = () => { setSetup(null); setRecovery(null); setEnableCode(""); setStep({ password: "", code: "" }); setNewEmail(""); loadSec(); setSecOpen(true); };
  const startSetup = async () => { try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const enable2fa = async () => { try { const { data } = await api.post("/auth/2fa/enable", { code: enableCode }); setRecovery(data.recovery_codes); setSetup(null); setEnableCode(""); loadSec(); toast.success("Two-factor authentication enabled"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const disable2fa = async () => { try { await api.post("/auth/2fa/disable", step); toast.success("Two-factor authentication disabled"); setStep({ password: "", code: "" }); loadSec(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const regen = async () => { try { const { data } = await api.post("/auth/2fa/recovery-codes", step); setRecovery(data.recovery_codes); setStep({ password: "", code: "" }); loadSec(); toast.success("Recovery codes regenerated"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const changeEmail = async () => { try { await api.post("/auth/change-email", { new_email: newEmail, password: step.password, code: step.code }); toast.success("Email changed. Please sign in again"); await logout(); nav("/login"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.np.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pwForm.np !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: pwForm.cur, new_password: pwForm.np });
      if (data.token) localStorage.setItem("neec_token", data.token);
      toast.success("Password changed. Other sessions signed out");
      setPwForm({ cur: "", np: "", confirm: "" });
      setPwOpen(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };

  const load = () => api.get("/superadmin/clients").then((r) => { setClients(r.data); setSvcSel((s) => (s ? r.data.find((c) => c.id === s.id) || null : null)); loadOverview(includeInternal); }).catch(() => {});
  const statusOf = (c) => c.product_type === "neec_service" ? (c.account_status || "onboarding") : ((c.billing?.status || c.subscription_status || "").toLowerCase().replace(/ /g, "_").replace("grace", "past_due").replace("trial", "onboarding"));
  const matches = (c) => filter === "all" ? true : ["neec_pos", "neec_service"].includes(filter) ? (c.product_type || "neec_pos") === filter : statusOf(c) === filter || (!["awaiting_payment", "trialing"].includes(filter) && statusOf(c).includes(filter));
  const isCustomer = (c) => (c.account_type_effective || "customer") === "customer";
  const shown = clients.filter((c) => isCustomer(c) && matches(c));
  const internal = clients.filter((c) => !isCustomer(c) && matches(c));
  useEffect(() => { load(); loadUsage(includeInternal); loadOverview(includeInternal); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleInternal = (v) => { setIncludeInternal(v); loadUsage(v); loadOverview(v); };
  // Product-aware open: Service companies use the Service drawer; POS companies open Billing (never the Service API).
  const openById = (id) => { const c = clients.find((x) => x.id === id); if (!c) return; if (c.product_type === "neec_service") setSvcSel(c); else setBilling(c); };

  const onPosCreated = (data) => { setLastLink(data.activation_link); setForm(blank); load(); };

  const resend = async (c) => {
    try { const { data } = await api.post(`/superadmin/clients/${c.id}/resend-invite`); setLastLink(data.activation_link); toast.success("Activation invitation re-sent"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resetInvite = async (c) => {
    try { const { data } = await api.post(`/superadmin/clients/${c.id}/reset-invite`); setLastLink(data.reset_link); toast.success("Password reset invitation sent"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleActive = async (c) => {
    try { await api.put(`/superadmin/clients/${c.id}`, { active: !c.active }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.business_name}? This deletes the owner login and cannot be undone.`)) return;
    try { await api.delete(`/superadmin/clients/${c.id}`); toast.success("Client removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [confirmSuspend, setConfirmSuspend] = useState(null);
  const doToggle = async (c) => {
    if (c.active) { setConfirmSuspend(c); return; }  // suspending needs confirmation
    await toggleActive(c);
  };
  const confirmDoSuspend = async () => { const c = confirmSuspend; setConfirmSuspend(null); if (c) await toggleActive(c); };
  const [billing, setBilling] = useState(null); // client being billed
  const [platformOpen, setPlatformOpen] = useState(false);
  const openBilling = (c) => setBilling(c);
  const toggleStripe = async (c, kind, enabled) => {
    const name = c.business_name;
    const msg = kind === "stripe-billing"
      ? (enabled ? `Enable Stripe subscription billing for this POS client?\n\n${name}\n\nThis will allow this tenant to enter the NEEC billing lifecycle. No access restriction should occur until the configured billing start/trial conditions apply.`
        : `TURN OFF Stripe Billing for ${name}?\n\nEnforcement will also be turned off. Billing history is preserved. Type-safe confirm: this stops the Stripe lifecycle for this tenant.`)
      : (enabled ? `Enable billing ENFORCEMENT for ${name}?\n\nWhen the Stripe lifecycle is awaiting payment, suspended or cancelled, normal POS operations will be restricted (Billing, Support and Logout stay available). No data is deleted.`
        : `Turn OFF enforcement for ${name}? Any Stripe-billing restriction will be lifted; billing status stays visible.`);
    if (!window.confirm(msg)) return;
    if (kind === "stripe-billing" && !enabled && window.prompt(`Type the business name to confirm turning Stripe Billing OFF:`) !== name) { toast.error("Name did not match. No change made."); return; }
    const reason = window.prompt("Reason (recorded in audit, optional):") || undefined;
    try { await api.put(`/superadmin/clients/${c.id}/${kind}`, { enabled, reason }); toast.success(`${kind === "stripe-billing" ? "Stripe Billing" : "Enforcement"} ${enabled ? "enabled" : "disabled"} for ${name}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [usersFor, setUsersFor] = useState(null);
  const [entFor, setEntFor] = useState(null);
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Link copied"); };

  const [tickets, setTickets] = useState([]);
  const [replyDraft, setReplyDraft] = useState({});
  const loadTickets = () => api.get("/superadmin/support").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { loadTickets(); }, []);
  const sendReply = async (t) => {
    const message = (replyDraft[t.id] || "").trim();
    if (!message) { toast.error("Type a reply first"); return; }
    try {
      await api.post(`/superadmin/support/${t.id}/reply`, { message });
      toast.success("Reply sent");
      setReplyDraft({ ...replyDraft, [t.id]: "" });
      loadTickets();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setTicketStatus = async (t, status) => {
    try { await api.put(`/superadmin/support/${t.id}/status`, { status }); toast.success(`Ticket ${status}`); loadTickets(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [ticketTab, setTicketTab] = useState("open");
  const deleteTicket = async (t) => {
    if (!window.confirm(`Permanently delete this archived ticket?\n\n"${t.subject}"\n\nThe full conversation will be removed and this cannot be undone. (Audit history is preserved.)`)) return;
    try { await api.delete(`/superadmin/support/${t.id}`); toast.success("Ticket permanently deleted"); loadTickets(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  // ---- Features & Modules ----
  const [featClient, setFeatClient] = useState(null);
  const [featList, setFeatList] = useState([]);
  const [featBusy, setFeatBusy] = useState(false);
  const [confirmFeat, setConfirmFeat] = useState(null); // { f, next }
  const [featImpact, setFeatImpact] = useState(null);
  useEffect(() => { setFeatImpact(null); if (confirmFeat && !confirmFeat.next && featClient && ["customers_credit", "prepaid_accounts"].includes(confirmFeat.f.key)) api.get(`/superadmin/clients/${featClient.id}/features/${confirmFeat.f.key}/impact`).then((r) => setFeatImpact(r.data)).catch(() => {}); }, [confirmFeat]); // eslint-disable-line react-hooks/exhaustive-deps
  const [migClient, setMigClient] = useState(null);
  const openFeatures = async (c) => {
    setFeatClient(c); setFeatList([]);
    try { const { data } = await api.get(`/superadmin/clients/${c.id}/features`); setFeatList(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const applyFeature = async () => {
    if (!confirmFeat) return;
    setFeatBusy(true);
    try {
      await api.put(`/superadmin/clients/${featClient.id}/features/${confirmFeat.f.key}`, { enabled: confirmFeat.next });
      toast.success(`${confirmFeat.f.name} ${confirmFeat.next ? "enabled" : "disabled"}`);
      const { data } = await api.get(`/superadmin/clients/${featClient.id}/features`);
      setFeatList(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setFeatBusy(false); setConfirmFeat(null);
  };
  const actions = { load, openBilling, toggleStripe, doToggle, resend, resetInvite, setUsersFor, setEntFor, openFeatures, setMigClient, remove };
  const FEAT_CLS_STYLE = {
    core: "text-emerald-600 border-emerald-500/30",
    optional: "text-sky-600 border-sky-500/30",
    premium: "text-violet-600 border-violet-500/30",
    custom: "text-amber-600 border-amber-500/30",
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border bg-card flex items-center justify-between px-6">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center text-white font-head font-extrabold">N</div>
          <div>
            <p className="font-head font-bold leading-none flex items-center gap-2 flex-wrap">NEEC Super Admin <EnvBadge showLive /></p>
            <p className="text-[10px] text-emerald-500 font-semibold tracking-wider flex items-center gap-1"><Zap className="w-3 h-3" /> PLATFORM CONSOLE</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
          <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
          <Button variant="outline" size="sm" onClick={() => nav("/super-admin/watchtower")} className="border-emerald-500/40 text-emerald-700 dark:text-emerald-300" data-testid="sa-watchtower-btn"><RadioTower className="w-4 h-4 mr-1.5" /> Watchtower</Button>
          <Button variant="outline" size="sm" onClick={() => nav("/super-admin/customer-identity")} className="border-sky-500/40 text-sky-700 dark:text-sky-300" data-testid="sa-customer-identity-btn"><Fingerprint className="w-4 h-4 mr-1.5" /> Customer Identity Protection</Button>
          <Button variant="outline" size="sm" onClick={() => nav("/super-admin/index-maintenance")} className="border-indigo-500/40 text-indigo-700 dark:text-indigo-300" data-testid="sa-index-maintenance-btn"><Database className="w-4 h-4 mr-1.5" /> Index Maintenance</Button>
          <Button variant="outline" size="sm" onClick={() => setHelpOpen(true)} data-testid="sa-help-content-btn"><LifeBuoy className="w-4 h-4 mr-1.5" /> Help Content</Button>
          <Button variant="outline" size="sm" onClick={() => setWnOpen(true)} data-testid="sa-whats-new-btn"><Sparkles className="w-4 h-4 mr-1.5" /> What's New</Button>
          <Button variant="ghost" size="icon" onClick={() => setPwOpen(true)} title="Change password" data-testid="sa-change-password-btn"><KeyRound className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" onClick={openSecurity} title="Account security & 2FA" data-testid="sa-security-btn"><ShieldCheck className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/login"); }} data-testid="sa-logout"><LogOut className="w-4 h-4" /></Button>
        </div>
      </header>

      <HelpAdmin open={helpOpen} onOpenChange={setHelpOpen} />
      <WhatsNewAdmin open={wnOpen} onOpenChange={setWnOpen} />
      <AddCompanyDialog open={addOpen} onOpenChange={setAddOpen} onProvisioned={load} onPos={(b) => { setForm({ ...blank, ...b }); setOpen(true); }} />
      <ServiceClientDrawer client={svcSel} onClose={() => setSvcSel(null)} onChanged={load} />
      <div className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><Building2 className="w-7 h-7" /> NEEC Digital Companies</h1>
            <p className="text-muted-foreground">Provision Business Owner accounts and track their launch readiness. Owners set their own password. You never see it.</p>
          </div>
          <Button variant="outline" onClick={() => setPlatformOpen(true)} data-testid="sa-platform-billing-btn"><CreditCard className="w-4 h-4 mr-1.5" /> Platform Billing Settings</Button>
          <Button onClick={() => { setLastLink(null); setAddOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sa-new-client">
            <Plus className="w-4 h-4 mr-1.5" /> New Company
          </Button>
        </div>

        <PlatformSummary data={overview} includeInternal={includeInternal} onToggleInternal={toggleInternal} onOpen={openById} />

        <BillingDashboard refreshKey={clients.length + (svcSel ? 1 : 0)} includeInternal={includeInternal} onOpen={openById} />

        <div className="flex flex-wrap gap-1.5" data-testid="sa-filters">
          {[["all", "All"], ["neec_pos", "NEEC POS"], ["neec_service", "NEEC Service"], ["onboarding", "Onboarding"], ["trialing", "Trialing"], ["awaiting_payment", "Awaiting Payment"], ["active", "Active"], ["past_due", "Past Due"], ["suspended", "Suspended"], ["cancelled", "Cancelled"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === k ? "bg-emerald-600 text-white border-emerald-600" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid={`sa-filter-${k}`}>{l}</button>
          ))}
        </div>

        {lastLink && (
          <Card className="p-4 border-emerald-500/40 bg-emerald-500/5" data-testid="sa-last-link">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-1">Secure link (fallback if email not received)</p>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-muted px-2 py-1.5 rounded flex-1 truncate">{lastLink}</code>
              <Button size="sm" variant="outline" onClick={() => copy(lastLink)} data-testid="sa-copy-link"><Copy className="w-4 h-4" /></Button>
            </div>
          </Card>
        )}


        <div className="flex items-center gap-2 pt-2" data-testid="sa-customers-heading">
          <Building2 className="w-5 h-5" /><h2 className="font-head text-xl font-extrabold">Customer Businesses</h2><Badge variant="outline">{shown.length}</Badge>
        </div>
        {clients.length === 0 && (
          <Card className="p-10 text-center text-muted-foreground" data-testid="sa-empty">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No client accounts yet. Create one to send a secure activation invitation.
          </Card>
        )}
        {clients.length > 0 && shown.length === 0 && <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="sa-customers-empty">No customer businesses match this filter.</Card>}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {shown.map((c) => c.product_type === "neec_service"
            ? <ServiceClientCard key={c.id} c={c} onOpen={setSvcSel} onChanged={load} labels={overview?.labels} />
            : <PosClientCard key={c.id} c={c} a={actions} labels={overview?.labels} />)}
        </div>

        <section className="pt-6" data-testid="sa-internal-section">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <FlaskConical className="w-5 h-5 text-purple-600" /><h2 className="font-head text-xl font-extrabold">NEEC Test Internal Live Accounts</h2><Badge className="bg-purple-500/15 text-purple-700 border-purple-500/30">{internal.length}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Real live tenants used internally by NEEC for testing, plus non-production Sandbox environments. Excluded from all customer metrics by default. Everything created inside stays usable in the tenant.</p>
          {internal.length === 0 ? <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="sa-internal-empty">No internal test accounts{filter !== "all" ? " match this filter" : ""}.</Card> : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 rounded-2xl border border-purple-500/30 bg-purple-500/[0.03] p-4">
              {internal.map((c) => c.product_type === "neec_service"
                ? <ServiceClientCard key={c.id} c={c} onOpen={setSvcSel} onChanged={load} labels={overview?.labels} />
                : <PosClientCard key={c.id} c={c} a={actions} labels={overview?.labels} />)}
            </div>
          )}
        </section>

        <UsagePanel usage={usage} includeInternal={includeInternal} />

        <div className="pt-4">
          <div className="flex items-center gap-2 mb-3">
            <LifeBuoy className="w-6 h-6" />
            <h2 className="font-head text-2xl font-extrabold">Support &amp; Bug Reports</h2>
          </div>
          {(() => {
            const isArchived = (t) => t.status === "closed" || t.status === "resolved";
            const openList = tickets.filter((t) => !isArchived(t));
            const archivedList = tickets.filter(isArchived);
            const list = ticketTab === "open" ? openList : archivedList;
            return (
              <>
                <div className="flex gap-2 mb-4" data-testid="sa-ticket-tabs">
                  <Button size="sm" variant={ticketTab === "open" ? "default" : "outline"} onClick={() => setTicketTab("open")} data-testid="sa-ticket-tab-open" className={ticketTab === "open" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}>Open <Badge variant="outline" className="ml-1.5 bg-background/20">{openList.length}</Badge></Button>
                  <Button size="sm" variant={ticketTab === "archived" ? "default" : "outline"} onClick={() => setTicketTab("archived")} data-testid="sa-ticket-tab-archived">Closed / Archived <Badge variant="outline" className="ml-1.5">{archivedList.length}</Badge></Button>
                </div>
                {list.length === 0 ? (
                  <Card className="p-8 text-center text-muted-foreground" data-testid="sa-tickets-empty">No {ticketTab === "open" ? "open" : "archived"} tickets.</Card>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="sa-tickets">
                    {list.map((t) => (
                      <Card key={t.id} className="p-4" data-testid={`sa-ticket-${t.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-1.5 text-sm font-semibold flex-wrap">
                            {t.type === "bug" ? <Bug className="w-4 h-4 text-red-500" /> : <Lightbulb className="w-4 h-4 text-violet-500" />}
                            {t.number && <span className="mono text-xs text-muted-foreground">{t.number}</span>}{t.subject}
                            {t.product === "neec_service" && <Badge className="text-[10px] bg-violet-500/15 text-violet-600 border-violet-500/30 ml-1" data-testid={`sa-ticket-product-${t.id}`}>NEEC Service</Badge>}
                            {t.priority && t.priority !== "normal" && <Badge variant="outline" className="text-[10px] capitalize">{t.priority}</Badge>}
                          </div>
                          {!t.unavailable && (t.product === "neec_service" ? (
                            <select value={t.status === "resolved" ? "closed" : (t.status || "open")} onChange={(e) => setTicketStatus(t, e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid={`sa-ticket-status-${t.id}`}>
                              <option value="open">Open</option><option value="in_progress">In Progress</option><option value="closed">Resolved</option>
                            </select>
                          ) : (
                            <select value={t.status || "open"} onChange={(e) => setTicketStatus(t, e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid={`sa-ticket-status-${t.id}`}>
                              <option value="open">Open</option><option value="in_progress">In Progress</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
                            </select>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 break-all">{t.business_name}{t.subdomain ? ` (${t.subdomain})` : ""} · {t.by}{t.role ? ` (${t.role})` : ""}{t.requester_email ? ` · ${t.requester_email}` : ""}{t.requester_phone ? ` · ${t.requester_phone}` : ""} · {(t.created_at || "").slice(0, 10)}</p>
                        {t.product === "neec_service" && t.tenant_id && <p className="text-[10px] text-muted-foreground mono break-all" data-testid={`sa-ticket-tenant-${t.id}`}>tenant {t.tenant_id}{t.category ? ` · ${t.category}` : ""}</p>}
                        <p className="text-sm mt-2 whitespace-pre-wrap">{t.description}</p>
                        {t.email?.owner_status === "failed" && <p className="text-[11px] text-amber-600 mt-1">Owner email not delivered: {t.email.owner_error}</p>}
                        {(t.module || t.action) && <p className="text-xs text-muted-foreground mt-1">Where: {t.module || "-"} · Doing: {t.action || "-"}</p>}
                        {(t.replies || []).length > 0 && (
                          <div className="mt-3 space-y-2 border-t border-border pt-2">
                            {t.replies.map((r) => (
                              <div key={r.id} className="text-xs bg-secondary rounded-lg p-2">
                                <span className="font-semibold">{r.by}</span> <span className="text-muted-foreground">· {(r.at || "").slice(0, 16).replace("T", " ")}</span>
                                <p className="mt-0.5 whitespace-pre-wrap">{r.message}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-3">
                          <Input value={replyDraft[t.id] || ""} onChange={(e) => setReplyDraft({ ...replyDraft, [t.id]: e.target.value })} placeholder="Reply to the client…" data-testid={`sa-reply-input-${t.id}`} />
                          <Button size="sm" onClick={() => sendReply(t)} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid={`sa-reply-send-${t.id}`}><Send className="w-4 h-4" /></Button>
                          {isArchived(t) && (
                            <Button size="sm" variant="outline" onClick={() => deleteTicket(t)} className="shrink-0 border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10" data-testid={`sa-ticket-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
{/* END-TICKETS */}
        <LegacyFooter />
      </div>

      <PosOnboardingDialog open={open} onOpenChange={setOpen} initial={form} onCreated={onPosCreated} />

      <Dialog open={!!confirmSuspend} onOpenChange={(o) => { if (!o) setConfirmSuspend(null); }}>
        <DialogContent className="max-w-sm" data-testid="sa-suspend-confirm">
          <DialogHeader>
            <DialogTitle className="font-head">Suspend this business account?</DialogTitle>
            <DialogDescription>
              {confirmSuspend ? `${confirmSuspend.business_name} (${confirmSuspend.owner_name}) will be immediately signed out and blocked from logging in until you reactivate them. No data is deleted.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" onClick={() => setConfirmSuspend(null)} data-testid="sa-suspend-cancel">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmDoSuspend} data-testid="sa-suspend-confirm-btn">Suspend Account</Button>
          </div>
        </DialogContent>
      </Dialog>

      <TenantUsersModal client={usersFor} onClose={() => setUsersFor(null)} />
      <EntitlementsDialog client={entFor} onClose={() => setEntFor(null)} onChanged={load} />
      <BillingModal client={billing} onClose={() => setBilling(null)} onChanged={(data) => { setBilling(data); load(); }}
        onToggleActive={async (c) => { if (c.active) { setConfirmSuspend(c); } else { await toggleActive(c); const { data } = await api.get("/superadmin/clients"); const f = data.find((x) => x.id === c.id); if (f) setBilling(f); } }} />
      <PlatformBillingSettings open={platformOpen} onClose={() => setPlatformOpen(false)} />

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md" data-testid="sa-change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><KeyRound className="w-5 h-5" /> Change Super Admin Password</DialogTitle>
            <DialogDescription>Enter your current password, then set a new one. This signs out all other sessions.</DialogDescription>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-3">
            <div><Label className="text-xs">Current Password</Label><Input type="password" autoComplete="current-password" value={pwForm.cur} onChange={(e) => setPwForm({ ...pwForm, cur: e.target.value })} data-testid="sa-pw-current" /></div>
            <div><Label className="text-xs">New Password</Label><Input type="password" autoComplete="new-password" placeholder="At least 8 characters" value={pwForm.np} onChange={(e) => setPwForm({ ...pwForm, np: e.target.value })} data-testid="sa-pw-new" /></div>
            <div><Label className="text-xs">Confirm New Password</Label><Input type="password" autoComplete="new-password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="sa-pw-confirm" /></div>
            <Button type="submit" disabled={pwBusy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sa-pw-submit">{pwBusy ? "Saving…" : "Update Password"}</Button>
            <p className="text-xs text-muted-foreground">Your password is securely hashed and never stored or shown in plain text.</p>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={secOpen} onOpenChange={setSecOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="sa-security-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Account Security</DialogTitle>
            <DialogDescription>Protect your Super Admin account with two-factor authentication and recovery codes.</DialogDescription>
          </DialogHeader>
          {recovery ? (
            <div className="space-y-3" data-testid="sa-recovery-codes">
              <p className="text-sm font-semibold">Save your recovery codes</p>
              <p className="text-xs text-muted-foreground">Each code works once. Store them somewhere safe. They are shown only now and let you sign in if you lose your authenticator.</p>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-secondary rounded-lg p-3">
                {recovery.map((c) => <span key={c}>{c}</span>)}
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => copy(recovery.join("\n"))} data-testid="sa-copy-recovery">Copy codes</Button>
              <Button variant="outline" className="w-full" onClick={() => setRecovery(null)}>Done</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div><p className="text-sm font-medium">Two-factor authentication</p><p className="text-xs text-muted-foreground">{sec?.mfa_enabled ? `Enabled · ${sec?.recovery_codes_remaining ?? 0} recovery codes left` : "Disabled"}</p></div>
                <Badge variant="outline" className={sec?.mfa_enabled ? "text-emerald-600 border-emerald-500/40" : ""} data-testid="sa-2fa-status">{sec?.mfa_enabled ? "ON" : "OFF"}</Badge>
              </div>

              {!sec?.mfa_enabled && !setup && (
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={startSetup} data-testid="sa-2fa-setup">Set up authenticator app</Button>
              )}
              {!sec?.mfa_enabled && setup && (
                <div className="space-y-2" data-testid="sa-2fa-setup-panel">
                  <p className="text-xs text-muted-foreground">Scan this QR in Google Authenticator, Microsoft Authenticator or Authy, then enter the 6-digit code.</p>
                  <img src={setup.qr} alt="2FA QR" className="w-40 h-40 mx-auto rounded-lg border border-border bg-white" />
                  <p className="text-[11px] text-center text-muted-foreground break-all">Secret: <span className="font-mono">{setup.secret}</span></p>
                  <Input placeholder="6-digit code" value={enableCode} onChange={(e) => setEnableCode(e.target.value)} data-testid="sa-2fa-enable-code" />
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={enable2fa} data-testid="sa-2fa-enable">Verify & Enable</Button>
                </div>
              )}
              {sec?.mfa_enabled && (
                <div className="space-y-2 rounded-lg border border-border p-3">
                  <p className="text-sm font-semibold">Manage 2FA</p>
                  <p className="text-xs text-muted-foreground">These actions require your current password and a current authenticator (or recovery) code.</p>
                  <Input type="password" placeholder="Current password" value={step.password} onChange={(e) => setStep({ ...step, password: e.target.value })} data-testid="sa-step-password" />
                  <Input placeholder="Authenticator or recovery code" value={step.code} onChange={(e) => setStep({ ...step, code: e.target.value })} data-testid="sa-step-code" />
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={regen} data-testid="sa-2fa-regen">Regenerate codes</Button>
                    <Button variant="outline" className="flex-1 text-rose-500 border-rose-500/40" onClick={disable2fa} data-testid="sa-2fa-disable">Disable 2FA</Button>
                  </div>
                </div>
              )}

              <div className="space-y-2 rounded-lg border border-border p-3">
                <p className="text-sm font-semibold">Change login / recovery email</p>
                <p className="text-xs text-muted-foreground">Current: <span className="font-mono">{sec?.email}</span>. Requires your password{sec?.mfa_enabled ? " and authenticator code" : ""}; you'll be signed out.</p>
                <Input placeholder="New email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="sa-new-email" />
                <Input type="password" placeholder="Current password" value={step.password} onChange={(e) => setStep({ ...step, password: e.target.value })} data-testid="sa-email-password" />
                {sec?.mfa_enabled && <Input placeholder="Authenticator code" value={step.code} onChange={(e) => setStep({ ...step, code: e.target.value })} data-testid="sa-email-code" />}
                <Button className="w-full" variant="outline" onClick={changeEmail} data-testid="sa-change-email">Change Email</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!featClient} onOpenChange={(o) => { if (!o) { setFeatClient(null); setFeatList([]); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sa-features-modal">
          <DialogHeader>
            <DialogTitle className="font-head">Features &amp; Modules: {featClient?.business_name}</DialogTitle>
            <DialogDescription>
              Enable or disable Optional, Premium and Custom modules for this tenant only. Core features are always on; Core Standard modules
              marked "included" are part of the Standard plan (Customer Credit is switched on by the Business Owner from Settings).
              Disabling a module hides it and blocks its API but never deletes its data.
            </DialogDescription>
          </DialogHeader>
          {featClient && <SaIndustryCard clientId={featClient.id} />}
          {featClient && <SaDepartmentsCard clientId={featClient.id} />}
          {featList.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="sa-features-loading">Loading features…</p>
          ) : (
            <div className="space-y-2.5">
              {featList.map((f) => (
                <div key={f.key} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid={`sa-feature-${f.key}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{f.name}</p>
                      <Badge variant="outline" className={`text-[10px] capitalize shrink-0 ${FEAT_CLS_STYLE[f.classification]}`}>{f.classification}</Badge>
                      {f.entitlement === "standard" && f.classification !== "core" && (
                        <Badge className="text-[10px] shrink-0 bg-emerald-500/15 text-emerald-600 border-emerald-500/30" data-testid={`sa-feature-entitlement-${f.key}`}>Core Standard · included{f.owner_activatable ? " · Owner-activated" : ""}</Badge>)}
                    </div>
                    <p className="text-xs text-muted-foreground">{f.description}</p>
                    {f.source && f.toggleable && (
                      <p className="text-[10px] text-muted-foreground mt-0.5" data-testid={`sa-feature-source-${f.key}`}>
                        Effective: {f.enabled ? "ON" : "OFF"} · {f.source === "tenant" ? "explicit tenant setting" : f.source === "industry" ? "industry profile default" : "platform default"}
                      </p>
                    )}
                    {f.updated_by && (
                      <p className="text-[10px] text-muted-foreground mt-0.5" data-testid={`sa-feature-meta-${f.key}`}>
                        {f.enabled ? "Enabled" : "Last changed"} {(f.updated_at || f.enabled_at || "").slice(0, 10)} · by {f.updated_by}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!f.toggleable
                      ? <Badge className="text-[10px] bg-emerald-500/15 text-emerald-600 border-emerald-500/30" data-testid={`sa-feature-alwayson-${f.key}`}>Always on</Badge>
                      : <Switch checked={f.enabled} onCheckedChange={(v) => setConfirmFeat({ f, next: v })} data-testid={`sa-feature-toggle-${f.key}`} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmFeat} onOpenChange={(o) => { if (!o) setConfirmFeat(null); }}>
        <DialogContent className="max-w-sm" data-testid="sa-feature-confirm">
          <DialogHeader>
            <DialogTitle className="font-head">{confirmFeat?.next ? "Enable" : "Disable"} {confirmFeat?.f?.name}?</DialogTitle>
            <DialogDescription>
              {confirmFeat ? (confirmFeat.next
                ? `This will turn ON "${confirmFeat.f.name}" for ${featClient?.business_name} only. No other tenant is affected.`
                : `This will turn OFF "${confirmFeat.f.name}" for ${featClient?.business_name}. Its data and settings are kept and will return if you re-enable it.`) : ""}
            </DialogDescription>
            {featImpact && <p className="text-sm font-medium text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2" data-testid="sa-feature-impact">{featImpact.message}</p>}
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" onClick={() => setConfirmFeat(null)} data-testid="sa-feature-confirm-cancel">Cancel</Button>
            <Button disabled={featBusy} className={confirmFeat?.next ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"} onClick={applyFeature} data-testid="sa-feature-confirm-btn">
              {featBusy ? "Saving…" : (confirmFeat?.next ? "Enable" : "Disable")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!migClient} onOpenChange={(o) => { if (!o) setMigClient(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="sa-migrate-modal">
          <DialogHeader>
            <DialogTitle className="font-head">Import Data: {migClient?.business_name}</DialogTitle>
            <DialogDescription>Assist this client with a QuickBooks migration. All records are imported into this tenant only.</DialogDescription>
          </DialogHeader>
          {migClient && <MigrationWizard base={`/superadmin/clients/${migClient.id}/migration`} tenantLabel={migClient.business_name} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
